import { useEffect, useRef } from "react";
import {
  useAudioVideo,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import { toast } from "sonner";
import { useDevices } from "../providers/DevicesProvider";

export function useDeviceHotPlug() {
  const {
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    selectedAudioInputDevice,
    selectedAudioOutputDevice,
    selectedVideoInputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
    selectVideoInputDevice,
  } = useDevices();

  const audioVideo = useAudioVideo();
  const meetingManager = useMeetingManager();

  const prevAudioInputRef = useRef<MediaDeviceInfo[]>([]);
  const prevAudioOutputRef = useRef<MediaDeviceInfo[]>([]);
  const prevVideoInputRef = useRef<MediaDeviceInfo[]>([]);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!audioVideo) {
      hasInitializedRef.current = false;
      prevAudioInputRef.current = [];
      prevAudioOutputRef.current = [];
      prevVideoInputRef.current = [];
      return;
    }

    const timer = setTimeout(() => {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        prevAudioInputRef.current = audioInputDevices;
        prevAudioOutputRef.current = audioOutputDevices;
        prevVideoInputRef.current = videoInputDevices;
        return;
      }

      (async () => {
        // --- Audio Input ---
        const prevAudioIn = prevAudioInputRef.current;
        const addedAudioIn = audioInputDevices.filter(
          (d) => !prevAudioIn.find((p) => p.deviceId === d.deviceId),
        );
        const removedAudioIn = prevAudioIn.filter(
          (p) => !audioInputDevices.find((d) => d.deviceId === p.deviceId),
        );
        const selectedAudioInDisappeared = removedAudioIn.some(
          (d) => d.deviceId === selectedAudioInputDevice,
        );

        if (selectedAudioInDisappeared && audioInputDevices.length > 0) {
          const fallback = audioInputDevices[0];
          try {
            await selectAudioInputDevice(fallback.deviceId);
            await meetingManager.startAudioInputDevice(fallback.deviceId);
            toast(`Switched microphone to ${fallback.label}`, {
              duration: 5000,
            });
          } catch (e) {
            console.warn("[useDeviceHotPlug] Audio input fallback failed:", e);
          }
        } else if (addedAudioIn.length > 0 && !selectedAudioInDisappeared) {
          const newDevice = addedAudioIn[0];
          const previousDeviceId = selectedAudioInputDevice;
          try {
            await selectAudioInputDevice(newDevice.deviceId);
            await meetingManager.startAudioInputDevice(newDevice.deviceId);
            toast(`Switched microphone to ${newDevice.label}`, {
              duration: 8000,
              action: previousDeviceId
                ? {
                    label: "Undo",
                    onClick: async () => {
                      try {
                        await selectAudioInputDevice(previousDeviceId);
                        await meetingManager.startAudioInputDevice(
                          previousDeviceId,
                        );
                      } catch {
                        /* device may no longer exist */
                      }
                    },
                  }
                : undefined,
            });
          } catch (e) {
            console.warn("[useDeviceHotPlug] Audio input switch failed:", e);
          }
        }

        // --- Audio Output ---
        const supportsSetSinkId =
          new DefaultBrowserBehavior().supportsSetSinkId();

        if (supportsSetSinkId) {
          const prevAudioOut = prevAudioOutputRef.current;
          const addedAudioOut = audioOutputDevices.filter(
            (d) => !prevAudioOut.find((p) => p.deviceId === d.deviceId),
          );
          const removedAudioOut = prevAudioOut.filter(
            (p) => !audioOutputDevices.find((d) => d.deviceId === p.deviceId),
          );
          const selectedAudioOutDisappeared = removedAudioOut.some(
            (d) => d.deviceId === selectedAudioOutputDevice,
          );

          if (
            selectedAudioOutDisappeared &&
            audioOutputDevices.length > 0
          ) {
            const fallback = audioOutputDevices[0];
            try {
              await selectAudioOutputDevice(fallback.deviceId);
              await meetingManager.startAudioOutputDevice(fallback.deviceId);
              toast(`Switched speaker to ${fallback.label}`, {
                duration: 5000,
              });
            } catch (e) {
              console.warn(
                "[useDeviceHotPlug] Audio output fallback failed:",
                e,
              );
            }
          } else if (
            addedAudioOut.length > 0 &&
            !selectedAudioOutDisappeared
          ) {
            const newDevice = addedAudioOut[0];
            const previousDeviceId = selectedAudioOutputDevice;
            try {
              await selectAudioOutputDevice(newDevice.deviceId);
              await meetingManager.startAudioOutputDevice(newDevice.deviceId);
              toast(`Switched speaker to ${newDevice.label}`, {
                duration: 8000,
                action: previousDeviceId
                  ? {
                      label: "Undo",
                      onClick: async () => {
                        try {
                          await selectAudioOutputDevice(previousDeviceId);
                          await meetingManager.startAudioOutputDevice(
                            previousDeviceId,
                          );
                        } catch {
                          /* device may no longer exist */
                        }
                      },
                    }
                  : undefined,
              });
            } catch (e) {
              console.warn(
                "[useDeviceHotPlug] Audio output switch failed:",
                e,
              );
            }
          }
        }

        // --- Video Input (fallback only, no auto-switch on connect) ---
        const removedVideo = prevVideoInputRef.current.filter(
          (p) => !videoInputDevices.find((d) => d.deviceId === p.deviceId),
        );
        const selectedVideoDisappeared = removedVideo.some(
          (d) => d.deviceId === selectedVideoInputDevice,
        );

        if (selectedVideoDisappeared && videoInputDevices.length > 0) {
          const fallback = videoInputDevices[0];
          try {
            await selectVideoInputDevice(fallback.deviceId);
            const localTile = audioVideo.getLocalVideoTile();
            if (localTile?.state()?.active) {
              await meetingManager.startVideoInputDevice(fallback.deviceId);
            }
            toast(`Switched camera to ${fallback.label}`, { duration: 5000 });
          } catch (e) {
            console.warn(
              "[useDeviceHotPlug] Video input fallback failed:",
              e,
            );
          }
        }

        // Update refs
        prevAudioInputRef.current = audioInputDevices;
        prevAudioOutputRef.current = audioOutputDevices;
        prevVideoInputRef.current = videoInputDevices;
      })();
    }, 500);

    return () => clearTimeout(timer);
  }, [
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    audioVideo,
    selectedAudioInputDevice,
    selectedAudioOutputDevice,
    selectedVideoInputDevice,
    selectAudioInputDevice,
    selectAudioOutputDevice,
    selectVideoInputDevice,
    meetingManager,
  ]);
}
